/**
 * Lightweight logger. Replaces direct console.* usage so that:
 * - In production, debug/info calls are silenced (they were diagnostic only).
 * - All output is routed through one module so it can later be swapped for
 *   Sentry, Datadog, etc. without touching call sites.
 *
 * Use:
 *   import logger from "@/utils/logger";
 *   logger.debug("payload", payload);
 *   logger.error("request failed", error);
 */

const IS_PROD = process.env.NODE_ENV === "production";

type Level = "debug" | "info" | "warn" | "error";

const ENABLED: Record<Level, boolean> = {
  debug: !IS_PROD,
  info: !IS_PROD,
  warn: true,
  error: true,
};

function emit(level: Level, args: unknown[]): void {
  if (!ENABLED[level]) return;
  const fn =
    level === "debug"
      ? // eslint-disable-next-line no-console
        console.log
      : level === "info"
        ? // eslint-disable-next-line no-console
          console.info
        : level === "warn"
          ? // eslint-disable-next-line no-console
            console.warn
          : // eslint-disable-next-line no-console
            console.error;
  fn(...args);
}

export const logger = {
  debug: (...args: unknown[]) => emit("debug", args),
  info: (...args: unknown[]) => emit("info", args),
  warn: (...args: unknown[]) => emit("warn", args),
  error: (...args: unknown[]) => emit("error", args),
};

export default logger;